import React, { useState, useEffect } from 'react';
import {
    Syringe,
    FlaskConical,
    Package,
    FilePlus,
    ClipboardCheck
} from 'lucide-react';
import './SamplesHub.css';

// Import the content from existing panels (we'll render them as components)
import NursingPanel from './NursingPanel';
import LabPanel from './LabPanel';
import ShipmentTracker from '../components/ShipmentTracker';
import StudyRegistration from '../components/StudyRegistration';
import StudyTracking from '../components/StudyTracking';

const SamplesHub = ({ hasPermission: inheritedPermission }) => {

    const localHasPermission = (view) => {
        if (inheritedPermission) return inheritedPermission(view);

        try {
            const userData = localStorage.getItem('user');
            if (!userData) return false;
            const user = JSON.parse(userData);
            if (!user || !user.role) return false;

            const rolePermissions = {
                'superadmin': ['all'],
                'admin': ['all'],
                'doctor': ['laboratory', 'nursing', 'shipments', 'samples', 'reception'],
                'medico': ['laboratory', 'nursing', 'shipments', 'samples', 'reception'],
                'médico': ['laboratory', 'nursing', 'shipments', 'samples', 'reception'],
                'nurse': ['nursing', 'samples'],
                'enfermera': ['nursing', 'samples'],
                'receptionist': ['reception', 'samples', 'nursing', 'shipments', 'laboratory'],
                'reception': ['reception', 'samples', 'nursing', 'shipments', 'laboratory'],
                'recepcion': ['reception', 'samples', 'nursing', 'shipments', 'laboratory'],
                'recepción': ['reception', 'samples', 'nursing', 'shipments', 'laboratory'],
                'lab tech': ['laboratory', 'shipments', 'samples'],
                'laboratorio': ['laboratory', 'shipments', 'samples']
            };

            const roleName = (user.role.name || user.role).toLowerCase();
            const permissions = rolePermissions[roleName] || [];
            return permissions.includes('all') || permissions.includes(view);
        } catch (e) {
            return false;
        }
    };

    // Default to the first available tab for the user
    const getDefaultTab = () => {
        if (localHasPermission('reception') || localHasPermission('admin') || localHasPermission('medico')) return 'registration';
        if (localHasPermission('nursing')) return 'nursing';
        if (localHasPermission('laboratory')) return 'laboratory';
        if (localHasPermission('shipments')) return 'shipments';
        return 'tracking';
    };

    const [activeTab, setActiveTab] = useState(getDefaultTab());

    const allTabs = [
        {
            id: 'registration',
            label: 'Alta de Estudio',
            icon: FilePlus,
            description: 'Registrar nuevo estudio',
            color: '#00BBBF',
            permission: ['reception', 'nursing', 'admin', 'medico']
        },
        {
            id: 'nursing',
            label: 'Muestras',
            icon: Syringe,
            description: 'Toma de muestras',
            color: '#ec4899',
            permission: ['nursing', 'admin', 'medico', 'doctor']
        },
        {
            id: 'shipments',
            label: 'Envíos',
            icon: Package,
            description: 'Rastreo a labs externos',
            color: '#f59e0b',
            permission: ['shipments', 'admin', 'medico', 'doctor']
        },
        {
            id: 'laboratory',
            label: 'Laboratorio',
            icon: FlaskConical,
            description: 'Procesamiento y resultados',
            color: '#8b5cf6',
            permission: ['laboratory', 'admin', 'medico', 'doctor']
        },
        {
            id: 'tracking',
            label: 'Seguimiento',
            icon: ClipboardCheck,
            description: 'Estado y verificación',
            color: '#06b6d4',
            permission: ['nursing', 'laboratory', 'shipments', 'samples', 'reception', 'admin', 'medico']
        }
    ];

    const tabs = allTabs.filter(tab => {
        // If user has any of the required permissions for this tab, show it
        return tab.permission.some(perm => localHasPermission(perm));
    });

    const renderContent = () => {
        switch (activeTab) {
            case 'registration':
                return <StudyRegistration />;
            case 'nursing':
                return <NursingPanel embedded={true} />;
            case 'laboratory':
                return <LabPanel embedded={true} />;
            case 'shipments':
                return <ShipmentTracker embedded={true} />;
            case 'tracking':
                return <StudyTracking embedded={true} />;
            default:
                return null;
        }
    };

    return (
        <div className="samples-hub-container">
            <div className="samples-hub-header">
                <div className="samples-title-wrapper">
                    <div className="samples-title">
                        <FlaskConical size={28} />
                        <h2>Estudios</h2>
                    </div>
                    <p>Gestión de estudios, muestras, envíos y laboratorio</p>
                </div>
            </div>

            <div className="samples-tabs">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            className={`samples-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                            style={{ '--tab-color': tab.color }}
                        >
                            <Icon size={20} />
                            <span className="tab-label">{tab.label}</span>
                            <span className="tab-description">{tab.description}</span>
                        </button>
                    );
                })}
            </div>

            <div className="samples-content">
                {renderContent()}
            </div>
        </div>
    );
};

export default SamplesHub;
